import { defineStore } from "pinia";
import { User } from "@/models/user/User";
import { Invitation } from "@/models/user/Invitation";
import { ProductionMembership } from "@/models/productions/ProductionMembership";
import { OrganizationMembership } from "@/models/organizations/OrganizationMembership";
import { MembershipStatus } from "@/models/enums/MembershipStatus";
import { EntityStatus } from "@/models/enums/EntityStatus";
import { MembershipRole } from "@/models/enums/Role";
import { useApplication } from "@/store/pinia/applicationStore";
import { NotificationItem } from "@/models/notifications/Notification";
import { UserService } from "@/services/UserService";
import router from "@/router";
import { ProductionService } from "@/services/ProductionService";
import { OrganizationService } from "@/services/OrganizationService";
import { WebAnalytics } from "@/modules/webAnalytics";
import moment from "moment";
import { getGenderIdentity } from "@/models/enums/GenderIdentity";
import { getPrimaryIndustry } from "@/models/enums/PrimaryIndustry";
import { getInitialPersona } from "@/models/enums/InitialPersona";
import { getUserType } from "@/models/enums/UserType";
import posthog from "posthog-js";
import { UserMetadata } from "@/models/user/UserMetadata";
import { FeedMetadata } from "@knocklabs/client/dist/types/clients/feed/interfaces";
import { useOnboardings } from "@/store/pinia/onboardingStore";
import { useRoute } from "vue-router";
import { getOrganizationIndustry } from "@/models/enums/OrganizationIndustry";
import { getProductionType } from "@/models/enums/ProductionType";
import { Production } from "@/models/productions/Production";
import { Organization } from "@/models/organizations/Organization";
import { MembershipsService } from "@/services/MembershipsService";
import { env } from "@/env";
import { bus } from "@/modules/eventBus";
import { MembershipsDto } from "@/models/memberships/MembershipsDto";

export const useUser = defineStore("user", {
	state: () => {
		return {
			profile: {} as User,
			metadata: {} as UserMetadata,
			invitations: [] as Array<Invitation>,
			productions: [] as Array<ProductionMembership>,
			organizations: [] as Array<OrganizationMembership>,
			memberships: {} as MembershipsDto,
			notifications: [] as Array<NotificationItem>,
			notificationsMetadata: { unread_count: 0, total_count: 0, unseen_count: 0 } as FeedMetadata,
			orgFetchComplete: false,
			prodFetchComplete: false,
			userFetchComplete: false,
			organizationData: [] as Array<Organization>,
			productionData: [] as Array<Production>,
			isHydrated: false,
			_hydratePromise: null as Promise<void> | null,
		};
	},
	actions: {
		ensureHydrated(): Promise<void> {
			if (this.isHydrated) return Promise.resolve();
			if (this._hydratePromise) return this._hydratePromise;

			this._hydratePromise = this.fetchMemberships()
				.catch((e) => {
					// make sure future calls can retry if the first one failed
					this._hydratePromise = null;
					throw e;
				})
				.finally(() => {
					// keep the promise if succeeded (optional), or null it out either way
					// leaving it null lets you retry later if needed
				});

			return this._hydratePromise;
		},
		fetchUserMetadata: function () {
			UserService.fetchMetadata().then((response) => {
				this.metadata = response.data;
			});
		},
		fetchInvitations: function () {
			UserService.fetchInvitations().then((response) => {
				this.invitations = response.data;
			});
		},
		fetchUser: function () {
			const applicationStore = useApplication();
			//console.log('[SH] fetchUser')
			//console.trace('[SH] fetchUser trace')
			UserService.fetch().then((response) => {
				if (!response.data.userExists) {
					router.push({ name: "sign-up" });
					applicationStore.setLoadingState(true);
				} else {
					this.profile = response.data;
					const onboardings = useOnboardings();
					onboardings.userProfile.title = this.profile.title;
					applicationStore.setLoadingState(true);
					applicationStore.setAuthenticatedState(true);
					this.userFetchComplete = true;
					// Identify only after memberships have landed - WebAnalytics.getHighestRole()
					// reads user.productions/organizations, which are empty until then.
					Promise.all([this.fetchInvitations(), this.fetchMemberships()]).then(() => {
						this.userFetchComplete = true;

						WebAnalytics.identify({
							id: this.profile.accountId,
							email: this.profile.emailAddresses.filter((x) => x.isPrimary)[0].email,
							firstName: this.profile.firstName,
							lastName: this.profile.lastName,
							name: `${this.profile.firstName} ${this.profile.lastName}`,
							title: this.profile.title,
							avatar: this.profile.avatar,
							$avatar: this.profile.avatar,
							StageCode: this.profile.stageCode,
							createdAt: moment(this.profile.createdOn).format(),
							pronouns: this.profile.pronouns,
							gender: getGenderIdentity(this.profile.genderIdentity).name,
							birthday: moment(this.profile.birthdate).format(),
							age: moment().diff(moment(this.profile.birthdate), "years", false),
							industry: getPrimaryIndustry(this.profile.industry).name,
							initialPersona: getInitialPersona(this.profile.initialPersona).name,
							userType: getUserType(this.profile.userType).name,
						});
					});

					UserService.fetchMetadata().then((metadataResponse) => {
						this.metadata = metadataResponse.data;
						if (this.metadata.featurebaseJwt) {
							bus.emit("messenger:init:user", this.metadata.featurebaseJwt);
						} else {
							bus.emit("messenger:init");
						}
					});

					UserService.reconcile();
				}
			});
		},
		async fetchMemberships() {
			const response = await MembershipsService.list();
			this.memberships = response.data;
			await this.handleProductionMemberships(response.data.productions);
			await this.handleOrganizationMemberships(response.data.organizations);
			this.isHydrated = true;
		},
		async handleProductionMemberships(memberships: ProductionMembership[]) {
			this.productions = memberships;

			ProductionService.listNew().then((result) => {
				this.productionData = result.data;
			});
			this.prodFetchComplete = true;

			// Group Identify
			this.productions.forEach((prod) => {
				WebAnalytics.identifyProduction({
					name: prod.name,
					productionId: prod.productionId,
					$avatar: prod.photo,
					StageCode: prod.stageCode,
					$name: prod.name,
					type: getProductionType(prod.type).name,
				});
			});
		},
		async handleOrganizationMemberships(memberships: OrganizationMembership[]) {
			this.organizations = memberships;

			OrganizationService.listNew().then((response) => {
				this.organizationData = response.data;
			});
			this.orgFetchComplete = true;

			// We only want to add elevated users of an organization to Intercom
			const companies = this.organizations
				.filter((x) => x.role !== MembershipRole.Member)
				.map((org) => {
					return {
						company_id: org.organizationId,
						created_at: Math.floor(new Date(org.creationTime).getTime() / 1000),
						name: org.name,
						website: org.website,
						phone: org.phone,
						email: org.email,
						industry: getOrganizationIndustry(org.industry).name,
					};
				}) as [];

			// Group Identify
			this.organizations.forEach((org) => {
				WebAnalytics.identifyOrganization({
					name: org.name,
					$avatar: org.photo,
					StageCode: org.stageCode,
					$name: org.name,
					organizationId: org.organizationId,
					website: org.website,
					email: "",
					phone: "",
					type: getOrganizationIndustry(org.industry).name,
					industry: getOrganizationIndustry(org.industry).name,
				});
			});
		},
		fetchNotifications: function () {
			UserService.fetchNotifications().then((response) => {
				this.notifications = response.data;
			});
			if (env.SH_ENV !== "Local") {
				const $this = this;
				setInterval(function () {
					UserService.fetchNotifications().then((response) => {
						$this.notifications = response.data;
					});
					$this.fetchMemberships();
				}, 15000);
			}
		},
		setNotificationAsRead: function (notificationId: string) {
			UserService.markReadNotifications([notificationId]).then(() => {
				this.notifications.forEach((notification) => {
					if (notification.notificationId === notificationId) {
						notification.read = true;
					}
				});
			});
		},
		markAllNotificationsRead: function () {
			// Extract all notificationIds from this.notifications array
			const notificationIds = this.notifications.map((notification) => notification.notificationId);

			// Pass the notificationIds array to the UserService
			UserService.markReadNotifications(notificationIds).then(() => {
				this.notifications.forEach((notification) => {
					// Check if the notificationId is in the notificationIds array
					if (notificationIds.includes(notification.notificationId)) {
						notification.read = true;
					}
				});
			});
		},
		lookupProductionId(productionId) {
			try {
				return this.productions.find((production) => production.productionId === productionId);
			} catch (ex) {
				return null;
			}
		},
		lookupOrganizationId(organizationId) {
			try {
				return this.organizations.find((organization) => organization.organizationId === organizationId);
			} catch (ex) {
				return null;
			}
		},
		canAccess(entityId) {
			const production = this.productions.find((p) => p.productionId === entityId);
			if (production) {
				return true;
			}
			const organization = this.organizations.find((p) => p.organizationId === entityId);
			return !!organization;
		},
		canManage(entityId): boolean {
			const production = this.productions.find((p) => p.productionId === entityId);
			if (!!production && production.role !== MembershipRole.Member) {
				return true;
			}
			const organization = this.organizations.find((p) => p.organizationId === entityId);
			return (organization && organization?.role !== MembershipRole.Member) || false;
		},
		isAdmin(entityId) {
			const production = this.productions.find((p) => p.productionId === entityId);
			if (production && (production.role === MembershipRole.Administrator || production.role === MembershipRole.Owner)) {
				return true;
			}
			const organization = this.organizations.find((p) => p.organizationId === entityId);
			return organization && (organization.role === MembershipRole.Administrator || organization.role === MembershipRole.Owner);
		},
		overrideUserTerms() {
			this.profile.tosAcceptedOn = new Date();
			this.profile.privacyPolicyAcceptedOn = new Date();
		},
		getProductionNameById(productionId: string) {
			const production = this.productions.find((p) => p.productionId === productionId);
			return production ? production.name : "";
		},
	},
	getters: {
		fetchComplete: (state) => {
			return state.prodFetchComplete && state.orgFetchComplete;
		},
		// lookupProductionId: (state) => {
		//     return (productionId) => state.productions.find(production => production.productionId === productionId);
		// },
		// lookupOrganizationId: (state) => {
		//     return (organizationId) => state.organizations.find(organization => organization.organizationId === organizationId);
		// },
		hasAccessToEntities: (state) => {
			return state.productions.filter((p) => p.status == MembershipStatus.Active).length > 0 || state.organizations.filter((o) => o.status == MembershipStatus.Active).length > 0;
		},
		canManageAny: (state) => {
			let result = state.productions.filter((p) => p.role !== MembershipRole.Member).length > 0;
			if (!result) {
				result = state.organizations.filter((p) => p.role !== MembershipRole.Member).length > 0;
			}
			return result;
		},
		canManageAnyActive: (state) => {
			const canManageActiveMembership = (membership: ProductionMembership | OrganizationMembership) => {
				const entityIsActive = membership.entityStatus === EntityStatus.Active || membership.entityStatus === EntityStatus.LegacyActive;
				return entityIsActive && membership.status === MembershipStatus.Active && membership.role !== MembershipRole.Member;
			};

			return state.productions.some(canManageActiveMembership) || state.organizations.some(canManageActiveMembership);
		},
		// canAccess: (state) => {
		//     return (entityId) => {
		//         const production = state.productions.find(p => p.productionId === entityId);
		//         if (production) {
		//             return true;
		//         }
		//         const organization = state.organizations.find(p => p.organizationId === entityId);
		//         return !!organization;
		//     }
		// },
		// canManage: (state) => {
		//     return (entityId) => {
		//         const production = state.productions.find(p => p.productionId === entityId);
		//         if (production && (production.role !== MembershipRole.Member)) {
		//             return true;
		//         }
		//         const organization = state.organizations.find(p => p.organizationId === entityId);
		//         return !!(organization && (organization.role !== MembershipRole.Member));
		//     }
		// },
		// isAdmin: (state) => {
		//     return (entityId) => {
		//         const production = state.productions.find(p => p.productionId === entityId);
		//         if (production && (production.role === MembershipRole.Administrator || production.role === MembershipRole.Owner)) {
		//             return true;
		//         }
		//         const organization = state.organizations.find(p => p.organizationId === entityId);
		//         return !!(organization && (organization.role === MembershipRole.Administrator || organization.role === MembershipRole.Owner));
		//     }
		// },
		currentEntity: () => {
			const applicationStore = useApplication();
			const route = applicationStore.route;
			if (route.name.indexOf("organization") > -1) {
				return route.params["organizationId"] as string;
			} else {
				return route.params["productionId"] as string;
			}
		},
		currentEntityObject: (state) => {
			const applicationStore = useApplication();
			const route = applicationStore.route;
			if (route.name.indexOf("organization") > -1) {
				const entityId = route.params["organizationId"] as string;
				return state.organizations.find((organization) => organization.organizationId === entityId);
			} else {
				const entityId = route.params["productionId"] as string;
				return state.productions.find((production) => production.productionId === entityId);
			}
			return { organizationId: "", productionId: "" };
		},
		currentEntityName: (state) => {
			const applicationStore = useApplication();
			const route = applicationStore.route;
			try {
				if (route.name.indexOf("organization") > -1) {
					const entityId = route.params["organizationId"] as string;
					const organization = state.organizations.find((organization) => organization.organizationId === entityId);
					return organization?.name;
				} else {
					const entityId = route.params["productionId"] as string;
					const production = state.productions.find((production) => production.productionId === entityId);
					return production?.name;
				}
			} catch (err) {
				return "";
			}
		},
		incomingInvitations: (state) => {
			return state.invitations.filter((x) => x.status === MembershipStatus.PendingUser);
		},
		outgoingRequests: (state) => {
			return state.invitations.filter((x) => x.status === MembershipStatus.PendingEntity);
		},
		outdatedTerms: (state) => {
			if (env.ANALYTICS === "enabled") {
				const policyDateFlag = posthog.getFeatureFlag("policy_date") as string;
				if (policyDateFlag !== "") {
					return moment(state.profile.tosAcceptedOn).isBefore(moment(policyDateFlag)) || moment(state.profile.privacyPolicyAcceptedOn).isBefore(moment(policyDateFlag));
				} else {
					return false;
				}
			} else {
				return false;
			}
		},
		isOwnProfile: (state) => {
			const route = useRoute();
			if (!!route?.params && route.params?.id !== undefined) {
				const id = route.params["id"];
				//console.log('isOwnProfile', state.profile.accountId, id)
				return state.profile.accountId === id;
			}
			return false;
		},
	},
});
